/**
 * Markup del tablón de NOTAMs — Fase 10a.1 (read-only).
 *
 * El sprite `fondo_notam.png` actúa como skin del wood-bg del chrome
 * (capa 0). Trae ya pintados: marco, lámparas, escudo del 51 a la
 * izquierda y el cartel "NOTAM BOARD - NOTICE TO AIR MISSIONS". El
 * frontend solo coloca encima:
 *
 *  - La hoja central con los párrafos NOTAM (CSS puro — papel crema).
 *  - Los post-its en sus coordenadas `xPercent`/`yPercent`.
 *  - La foto del Hornet (decorativa, top-right del corcho).
 *  - El pin de Discord (bottom-left, link al servidor).
 *
 * Todas las coordenadas son porcentajes relativos al `.notams-stage`
 * (cubre el `.chrome-main` al 100%), así el layout es estable a
 * cualquier resolución sin recalcular posiciones.
 */

import {
  hasRole,
  notamPostItColor,
  notamPostItPin,
  type AuthenticatedPilot,
  type Notam,
  type NotamPostit,
  type Role,
} from '@51esc/shared';

/**
 * Servidor Discord del 51 — IDs por defecto. Apuntamos directamente
 * a un canal concreto del guild — NO a una invitación
 * (`discord.gg/<code>`), porque ese flujo en clientes ya miembros del
 * server simplemente recarga la misma pantalla.
 *
 * Desde Fase 11g estos valores son configurables desde el panel
 * admin (`GET /api/settings`). El shell inicial renderiza los
 * defaults, y `applyDiscordSettings()` los actualiza en runtime con
 * los valores reales tras el fetch.
 */
const DEFAULT_DISCORD_GUILD_ID = '267245594551123969';
const DEFAULT_DISCORD_CHANNEL_ID = '267245594991394817';

function buildDiscordUrls(
  guildId: string,
  channelId: string,
): { web: string; app: string } {
  return {
    web: `https://discord.com/channels/${guildId}/${channelId}`,
    app: `discord://discord.com/channels/${guildId}/${channelId}`,
  };
}

const DEFAULT_DISCORD_URLS = buildDiscordUrls(
  DEFAULT_DISCORD_GUILD_ID,
  DEFAULT_DISCORD_CHANNEL_ID,
);
const DISCORD_URL = DEFAULT_DISCORD_URLS.web;
const DISCORD_APP_URL = DEFAULT_DISCORD_URLS.app;

/**
 * Actualiza el `<a class="notams-discord">` con los IDs efectivos
 * cargados desde el backend. Cae a los defaults si alguno de los IDs
 * es `null`. Llamar después de pintar el shell, una vez se tenga la
 * respuesta de `GET /api/settings`.
 */
export function applyDiscordSettings(
  root: HTMLElement,
  settings: { guildId: string | null; channelId: string | null },
): void {
  const anchor = root.querySelector<HTMLAnchorElement>('.notams-discord');
  if (!anchor) return;
  const guildId = settings.guildId ?? DEFAULT_DISCORD_GUILD_ID;
  const channelId = settings.channelId ?? DEFAULT_DISCORD_CHANNEL_ID;
  const urls = buildDiscordUrls(guildId, channelId);
  anchor.href = urls.web;
  anchor.dataset['discordWeb'] = urls.web;
  anchor.dataset['discordApp'] = urls.app;
}

export function renderNotamsShell(): string {
  return `
    <div class="notams-stage">
      <!-- Foto decorativa del Hornet, con chincheta. La etiqueta
           GALERÍA va en el margen blanco inferior, estilo Polaroid.
           Click sobre el Polaroid lleva a /galeria.html (Fase 10d). -->
      <a
        class="notams-hornet"
        href="/galeria.html"
        title="Galería de screenshots"
      >
        <img
          class="notams-hornet-img"
          src="/notams/fotohornet.png"
          alt=""
        />
        <span class="notams-hornet-label">GALERÍA</span>
      </a>

      <!-- Mapa decorativo bajo el Hornet. Los post-its se pueden
           colocar encima (la capa de post-its tiene z-index mayor). -->
      <img
        class="notams-map"
        src="/notams/mapa.png"
        alt=""
        aria-hidden="true"
      />

      <!-- Hoja de instrucciones en la banda metálica izquierda, bajo
           el escudo del 51. Texto rápido sobre los post-its: tres
           colores según destinatario/visibilidad y caducidad 5 días. -->
      <aside class="notams-howto" aria-label="Cómo usar los post-its">
        <h3 class="notams-howto-title">POST-IT</h3>
        <ul class="notams-howto-list">
          <li>
            <span class="notams-howto-dot notams-howto-dot--yellow"></span>
            Al escuadrón
          </li>
          <li>
            <span class="notams-howto-dot notams-howto-dot--blue"></span>
            Privado a un piloto
          </li>
          <li>
            <span class="notams-howto-dot notams-howto-dot--pink"></span>
            Público a un piloto
          </li>
        </ul>
        <p class="notams-howto-foot">
          Click para leer entero.<br />
          Arrastra para colocarlo en el corcho<br />
          (no sobre hoja ni galería).<br />
          Caducan a 5 días.
        </p>
      </aside>

      <!-- Pin de Discord. El href apunta al fallback web — el handler
           del módulo intercepta el click para intentar el deep-link de
           la app de escritorio (discord://...) y, si no hay app, abre
           la web. Si JS falla por cualquier motivo, el navegador sigue
           el href y el usuario llega a la web igualmente. -->
      <a
        class="notams-discord"
        href="${DISCORD_URL}"
        data-discord-app="${DISCORD_APP_URL}"
        data-discord-web="${DISCORD_URL}"
        target="_blank"
        rel="noopener noreferrer"
        title="Servidor Discord del 51"
      >
        <img class="notams-discord-img" src="/notams/discrod.png" alt="Discord" />
      </a>

      <!-- Hoja central de NOTAMs (la rellena renderSheet) -->
      <article class="notams-sheet" aria-label="Hoja de NOTAMs">
        <header class="notams-sheet-header">NOTAMS</header>
        <ol class="notams-sheet-list" data-zone="sheet"></ol>
      </article>

      <!-- Capa de post-its (la rellena renderPostits) -->
      <div class="notams-postits" data-zone="postits"></div>
    </div>
  `;
}

/**
 * Renderiza el contenido de la hoja central. Cada NOTAM ocupa un
 * `<li>` con su texto y un pie discreto con autor + fecha publicada.
 * Si no hay NOTAMs, muestra un mensaje en cursiva.
 *
 * Si el caller es admin+, los items reciben la clase `--editable` para
 * que el CSS active hover/cursor pointer; el handler de click vive
 * en `mount.ts` y abre el editor de NOTAM.
 */
export function renderSheet(
  notams: Notam[],
  currentUser: AuthenticatedPilot | null,
): string {
  if (notams.length === 0) {
    return `<li class="notams-sheet-empty">— No hay NOTAMs activos. —</li>`;
  }
  // Anónimo (`currentUser === null`): la hoja es de solo lectura, ningún
  // item recibe la clase `--editable`.
  const isAdmin =
    currentUser !== null && hasRole(currentUser.role as Role, 'admin');
  return notams.map((n) => renderSheetItem(n, isAdmin)).join('');
}

function renderSheetItem(n: Notam, isAdmin: boolean): string {
  const cls = isAdmin
    ? 'notams-sheet-item notams-sheet-item--editable'
    : 'notams-sheet-item';
  return `
    <li
      class="${cls}"
      data-notam-id="${escapeHtml(n.id)}"
      ${isAdmin ? 'role="button" tabindex="0" aria-label="Editar NOTAM"' : ''}
    >
      <p class="notams-sheet-body">${escapeHtml(n.body)}</p>
      <footer class="notams-sheet-meta">
        <span class="notams-sheet-author">${escapeHtml(n.authorCallsign)}</span>
        <span class="notams-sheet-date">${formatDate(n.publishedAt)}</span>
      </footer>
    </li>
  `;
}

/**
 * Renderiza la capa de post-its. Cada post-it lleva su color derivado
 * (yellow/blue/pink) y la posición `xPercent`/`yPercent` aplicada como
 * estilo inline — son coordenadas únicas por elemento, no clases.
 *
 * El currentUser permite decidir si añadir botones EDITAR (autor) y
 * BORRAR (autor o admin+) en la vista expandida, así como marcar el
 * post-it como movible (`data-postit-can-move`) para el drag&drop.
 */
export function renderPostits(
  postits: NotamPostit[],
  currentUser: AuthenticatedPilot | null,
): string {
  return postits.map((p) => renderPostit(p, currentUser)).join('');
}

function renderPostit(
  p: NotamPostit,
  currentUser: AuthenticatedPilot | null,
): string {
  const color = notamPostItColor(p);
  const pin = notamPostItPin(p);
  const headerLine = p.targetPilotCallsign
    ? `Para ${escapeHtml(p.targetPilotCallsign)}`
    : 'Al escuadrón';

  // Anónimo (`currentUser === null`): no es autor ni admin → ningún
  // botón EDITAR/BORRAR ni capacidad de mover. Post-it de solo lectura.
  const isAuthor = currentUser !== null && p.authorId === currentUser.id;
  const isAdmin =
    currentUser !== null && hasRole(currentUser.role as Role, 'admin');
  const canEdit = isAuthor;
  const canDelete = isAuthor || isAdmin;
  const canMove = isAuthor || isAdmin;

  const actions = canEdit || canDelete
    ? `<div class="notams-postit-actions" data-postit-actions>
         ${canEdit
           ? '<button type="button" class="notams-postit-action notams-postit-action--edit" data-postit-action="edit">EDITAR</button>'
           : ''}
         ${canDelete
           ? '<button type="button" class="notams-postit-action notams-postit-action--delete" data-postit-action="delete">BORRAR</button>'
           : ''}
       </div>`
    : '';

  return `
    <div
      class="notams-postit notams-postit--${color}"
      data-postit-id="${escapeHtml(p.id)}"
      data-postit-pin="${pin}"
      data-postit-author="${escapeHtml(p.authorId)}"
      ${canMove ? 'data-postit-can-move="1"' : ''}
      role="button"
      tabindex="0"
      style="left: ${p.xPercent.toFixed(2)}%; top: ${p.yPercent.toFixed(2)}%;"
      aria-label="Post-it: ${escapeHtml(p.body.slice(0, 80))}"
    >
      <img
        class="notams-postit-pin"
        src="${pinSrc(pin)}"
        alt=""
        aria-hidden="true"
      />
      <span class="notams-postit-header">${headerLine}</span>
      <span class="notams-postit-body">${escapeHtml(p.body)}</span>
      <span class="notams-postit-meta">— ${escapeHtml(p.authorCallsign)}</span>
      ${actions}
    </div>
  `;
}

function pinSrc(pin: 'yellow' | 'blue' | 'green'): string {
  if (pin === 'yellow') return '/notams/chinamarilla.png';
  if (pin === 'blue') return '/notams/Chinazul.png';
  return '/notams/Chinverde.png';
}

/**
 * Banner de error sobre el corcho. Mismo patrón que `operaciones.ts`.
 */
export function renderError(message: string): string {
  return `<div class="notams-error">${escapeHtml(message)}</div>`;
}

/**
 * Sidebar ACCIONES de la página `/notams.html`. Cualquier piloto puede
 * crear post-its (el backend filtra por visibilidad luego). El segundo
 * botón "+ NUEVA NOTAM" lo añadimos solo si el caller es admin+ — la
 * hoja oficial la gestiona el mando.
 *
 * Para usuarios anónimos (`currentUser === null`) la sección ACCIONES
 * se renderiza sin botones: el tablón es de solo lectura.
 */
export function renderNotamsActions(
  currentUser: AuthenticatedPilot | null,
): string {
  if (currentUser === null) {
    return `
    <section class="chrome-actions notams-actions">
      <h2 class="chrome-actions-title">ACCIONES</h2>
      <ul class="notams-actions-list"></ul>
    </section>
  `;
  }
  const isAdmin = hasRole(currentUser.role as Role, 'admin');
  return `
    <section class="chrome-actions notams-actions">
      <h2 class="chrome-actions-title">ACCIONES</h2>
      <ul class="notams-actions-list">
        <li>
          <button
            type="button"
            class="notams-action-btn"
            data-notams-action="new-postit"
          >+ NUEVO POST-IT</button>
        </li>
        ${isAdmin
          ? `<li>
               <button
                 type="button"
                 class="notams-action-btn"
                 data-notams-action="new-notam"
               >+ NUEVA NOTAM</button>
             </li>
             <li>
               <button
                 type="button"
                 class="notams-action-btn"
                 data-notams-action="archived"
               >ARCHIVADOS</button>
             </li>`
          : ''}
      </ul>
    </section>
  `;
}

function formatDate(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${pad(d.getDate())}/${pad(d.getMonth() + 1)}/${String(d.getFullYear()).slice(2)}`;
}

function escapeHtml(s: string): string {
  return s
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}
